import { Injectable } from "@angular/core";
import { Router, NavigationEnd, ActivatedRouteSnapshot, Data } from "@angular/router";
import { get } from "lodash";
import { BehaviorSubject } from "rxjs";
import { filter } from "rxjs/operators";
import { Breadcrumb } from "./breadcrumb.model";

@Injectable({ providedIn: 'root' })
export class BreadcrumbService {
  private readonly _breadcrumbs$ = new BehaviorSubject<Breadcrumb[]>(
    (() => {
      try {
        const path = window.location.pathname;
        if (path && path !== '/') {
          const parts = path.split('/').filter(p => p);
          const breadcrumbs: Breadcrumb[] = [{ label: 'Home', url: '/', disabled: false }];
          let currentUrl = '';
          parts.forEach((part, index) => {
            currentUrl += '/' + part;
            const label = part
              .replace(/([A-Z])/g, ' $1')
              .replace(/^./, str => str.toUpperCase())
              .trim();
            breadcrumbs.push({
              label,
              url: currentUrl,
              disabled: index === parts.length - 1
            });
          });
          return breadcrumbs;
        }
      } catch (e) {}
      return [];
    })()
  );
  readonly breadcrumbs$ = this._breadcrumbs$.asObservable();

  private dynamicBreadcrumbs: Map<string, Breadcrumb[]> = new Map();
  private isSuperAdmin: boolean = false;
  private previousUrl: string = '/Library/Questions'; // Default fallback

  constructor(private router: Router) {
    // Check user role from localStorage
    this.checkUserRole();
    
    // Track URL changes to store previous URL
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event: any) => {
        // Store previous URL before navigation (unless it's a revision history page)
        const currentUrl = this.router.url;
        if (!currentUrl.includes('RevisionHistory') && !currentUrl.includes('revisionHistory')) {
          this.previousUrl = currentUrl;
        }
      });
    
    // Generate breadcrumbs immediately for the current route
    const initialBreadcrumbs = this.buildBreadcrumbs(this.router.routerState.snapshot.root);
    if (initialBreadcrumbs && initialBreadcrumbs.length > 0) {
      this._breadcrumbs$.next(initialBreadcrumbs);
      sessionStorage.setItem("breadcrumbs", JSON.stringify(initialBreadcrumbs));
    }

    // Update breadcrumbs on navigation
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => {
        const breadcrumbs = this.buildBreadcrumbs(this.router.routerState.snapshot.root);
        const current = this._breadcrumbs$.value;
        if (!this.areBreadcrumbsEqual(current, breadcrumbs)) {
          this._breadcrumbs$.next(breadcrumbs);
          sessionStorage.setItem("breadcrumbs", JSON.stringify(breadcrumbs));
        }
      });
  }

  private areBreadcrumbsEqual(a: Breadcrumb[], b: Breadcrumb[]): boolean {
    if (!a || !b || a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) {
      if (a[i].label !== b[i].label) return false;
    }
    return true;
  }

  // Method to update user role
  setUserRole(isSuperAdmin: boolean) {
    this.isSuperAdmin = isSuperAdmin;
    const breadcrumbs = this.buildBreadcrumbs(this.router.routerState.snapshot.root);
    const current = this._breadcrumbs$.value;
    if (!this.areBreadcrumbsEqual(current, breadcrumbs)) {
      this._breadcrumbs$.next(breadcrumbs);
    }
  }

  private checkUserRole() {
    const userRole = localStorage.getItem('ur');
    this.isSuperAdmin = userRole === 'SA';
  }

  // Method to update breadcrumbs dynamically
  updateBreadcrumbs(breadcrumbs: Breadcrumb[]) {
    const current = this._breadcrumbs$.value;
    if (!this.areBreadcrumbsEqual(current, breadcrumbs)) {
      this._breadcrumbs$.next(breadcrumbs);
      sessionStorage.setItem("breadcrumbs", JSON.stringify(breadcrumbs));
    }
  }

  // Method to add dynamic breadcrumb for specific route
  setDynamicBreadcrumb(route: string, breadcrumbs: Breadcrumb[]) {
    this.dynamicBreadcrumbs.set(route, breadcrumbs);
    const current = this._breadcrumbs$.value;
    if (!this.areBreadcrumbsEqual(current, breadcrumbs)) {
      this._breadcrumbs$.next(breadcrumbs);
      sessionStorage.setItem("breadcrumbs", JSON.stringify(breadcrumbs));
    }
  }

  private buildBreadcrumbs(root: ActivatedRouteSnapshot): Breadcrumb[] {
    const breadcrumbs: Breadcrumb[] = [];
    this.addBreadcrumb(root, [], breadcrumbs);
    
    // Check if there are dynamic breadcrumbs for current route
    const currentUrl = this.router.url;
    
    // NEW: Label Revision History handling - use previous URL for Questions link
    if (currentUrl.includes('/Questions/labelRevisionHistory')) {
      return this.getLabelRevisionHistoryBreadcrumbs();
    }

    if (currentUrl.includes('/Questions/tagRevisionHistory')) {
      return this.getTagRevisionHistoryBreadcrumbs('Questions');
    }

    if (currentUrl.includes('/QuestionPapers/tagRevisionHistory')) {
      return this.getTagRevisionHistoryBreadcrumbs('QuestionPapers');
    }

    if (currentUrl.includes('/QuestionPapers/ProjectsRevisionHistory')) {
      return this.getProjectsRevisionHistoryBreadcrumbs();
    }

      // NEW: MOU Revision History handling (SAFE & ISOLATED)
  if (currentUrl.includes('/org-mou/RevisionHistory')) {
    return this.getMouRevisionHistoryBreadcrumbs();
  }
  if (currentUrl.includes('/Courses/CourseRevisionHistory')) {
    return this.getCourseRevisionHistoryBreadcrumbs();
  }
  if (currentUrl.includes('/QuestionPapers/QuestionPaperVersionHistory/CompareVersions')) {
    return this.getCompareVersionsBreadcrumbs();
  }
  if (/\/QuestionPapers\/QuestionPaperVersionHistory\/\d+\/All/.test(currentUrl)) {
    return this.getVersionRevisionHistoryBreadcrumbs();
  }
    if (this.dynamicBreadcrumbs.has(currentUrl)) {
      return this.dynamicBreadcrumbs.get(currentUrl)!;
    }
    
    // Modify breadcrumbs based on user role for edit organization page
    if (currentUrl.includes('/org/') && !this.isSuperAdmin) {
      return this.getNonAdminEditOrgBreadcrumbs();
    }
    
    return breadcrumbs;
  }

  private getLabelRevisionHistoryBreadcrumbs(): Breadcrumb[] {
    return [
      {
        label: 'Home',
        url: '/Dashboard',
        disabled: false,
      },
      {
        label: 'Library',
        url: '/Library',
        disabled: false,
      },
      {
        label: 'Questions',
        url: this.previousUrl, // Use stored previous URL instead of default
        disabled: false,
      },
      {
        label: 'Label Revision History',
        url: '',
        disabled: true,
      },
    ];
  }

  private getCourseRevisionHistoryBreadcrumbs(): Breadcrumb[] {
    const crumbs: Breadcrumb[] = [
      { label: 'Home', url: '/Dashboard', disabled: false },
      { label: 'Library', url: '/Library', disabled: false },
    ];

    if (this.previousUrl && this.previousUrl.includes('/Questions/')) {
      crumbs.push({ label: 'Questions', url: '/Library/Questions', disabled: false });
      
      const segment = this.previousUrl.split('/').pop()?.split('?')[0];
      if (segment === 'AddQuestion') {
        crumbs.push({ label: 'Add Question', url: this.previousUrl, disabled: false });
      } else if (segment === 'ModifyQuestion') {
        crumbs.push({ label: 'Modify Question', url: this.previousUrl, disabled: false });
      }
    } else {
      crumbs.push({ label: 'Courses', url: '/Library/Courses/', disabled: false });
    }

    crumbs.push({ label: 'Course Revision History', url: '', disabled: true });
    return crumbs;
  }

  private getCompareVersionsBreadcrumbs(): Breadcrumb[] {
    const crumbs: Breadcrumb[] = [
      { label: 'Home', url: '/Dashboard', disabled: false },
      { label: 'Question Paper', url: '/Library/QuestionPapers', disabled: false },
    ];

    const match = this.router.url.match(/QuestionPaperVersionHistory\/CompareVersions\/(\d+)\//);

    if (match) {
      const paperId = match[1];
      crumbs.push({
        label: 'Version History',
        url: `/Library/QuestionPapers/QuestionPaperVersionHistory/${paperId}`,
        disabled: false,
      });
      crumbs.push({
        label: 'Version Revision History',
        url: `/Library/QuestionPapers/QuestionPaperVersionHistory/${paperId}/All`,
        disabled: false,
      });
    }

    crumbs.push({ label: 'Compare Versions', url: '', disabled: true });
    return crumbs;
  }

  private getVersionRevisionHistoryBreadcrumbs(): Breadcrumb[] {
    const crumbs: Breadcrumb[] = [
      { label: 'Home', url: '/Dashboard', disabled: false },
      { label: 'Question Paper', url: '/Library/QuestionPapers', disabled: false },
    ];

    const match = this.router.url.match(/QuestionPaperVersionHistory\/(\d+)\/All/);

    if (match) {
      const paperId = match[1];
      crumbs.push({
        label: 'Version History',
        url: `/Library/QuestionPapers/QuestionPaperVersionHistory/${paperId}`,
        disabled: false,
      });
    }

    crumbs.push({ label: 'Version Revision History', url: '', disabled: true });
    return crumbs;
  }
  private getTagRevisionHistoryBreadcrumbs(section: 'Questions' | 'QuestionPapers'): Breadcrumb[] {
    const isQP = section === 'QuestionPapers';
    const sectionLabel = isQP ? 'Question Papers' : 'Questions';
    const sectionUrl = isQP ? '/Library/QuestionPapers' : '/Library/Questions';

    // Derive source page label from previousUrl
    let sourceLabel: string | null = null;
    let sourceUrl: string | null = null;
    if (this.previousUrl && this.previousUrl.includes(isQP ? '/QuestionPapers/' : '/Questions/')) {
      const segment = this.previousUrl.split('/').pop();
      if (segment === 'AddQuestion') { sourceLabel = 'Add Question'; sourceUrl = this.previousUrl; }
      else if (segment === 'ModifyQuestion') { sourceLabel = 'Modify Question'; sourceUrl = this.previousUrl; }
      else if (segment === 'AddQuestionPapers') { sourceLabel = 'Add Question Paper'; sourceUrl = this.previousUrl; }
      else if (segment === 'ModifyQuestionPapers') { sourceLabel = 'Modify Question Paper'; sourceUrl = this.previousUrl; }
    }

    const crumbs: Breadcrumb[] = [
      { label: 'Home', url: '/Dashboard', disabled: false },
      { label: 'Library', url: '/Library', disabled: false },
      { label: sectionLabel, url: sectionUrl, disabled: false },
    ];

    if (sourceLabel && sourceUrl) {
      crumbs.push({ label: sourceLabel, url: sourceUrl, disabled: false });
    }

    crumbs.push({ label: 'Tag Revision History', url: '', disabled: true });
    return crumbs;
  }

  private getProjectsRevisionHistoryBreadcrumbs(): Breadcrumb[] {
    let sourceLabel: string | null = null;
    let sourceUrl: string | null = null;

    if (this.previousUrl && this.previousUrl.includes('/QuestionPapers/')) {
      if (this.previousUrl.includes('AddEvaluationCriteria') || this.previousUrl.includes('AddQuestionPapers')) {
        sourceLabel = 'Add Question Paper';
        sourceUrl = this.previousUrl;
      } else if (this.previousUrl.includes('ModifyEvaluationCriteria') || this.previousUrl.includes('ModifyQuestionPapers')) {
        sourceLabel = 'Modify Question Paper';
        sourceUrl = this.previousUrl;
      }
    }

    const crumbs: Breadcrumb[] = [
      { label: 'Home', url: '/Dashboard', disabled: false },
      { label: 'Question Papers', url: '/Library/QuestionPapers', disabled: false },
    ];

    if (sourceLabel && sourceUrl) {
      crumbs.push({ label: sourceLabel, url: sourceUrl, disabled: false });
    }

    crumbs.push({ label: 'Project Revision History', url: '', disabled: true });
    return crumbs;
  }

  private getMouRevisionHistoryBreadcrumbs(): Breadcrumb[] {
  const encOrgId = localStorage.getItem('SelectedOrgEncId');

  if (this.isSuperAdmin) {
    return [
      {
        label: 'Home',
        url: '/org-list',
        disabled: false,
      },
      {
        label: 'Organizations',
        url: '/admin/org-list',
        disabled: false,
      },
      {
        label: 'MOU Revision History',
         url: '/admin/org-list',
        disabled: true,
      },
    ];
  }

  return [
    {
      label: 'Home',
      url: '/org-list',
      disabled: false,
    },
    {
      label: 'Edit Organization',
      url: `/admin/org/${encodeURIComponent(encOrgId!)}`, 
      disabled: false,
    },
    {
      label: 'MOU Revision History',
       url: '',
      disabled: true,
    },
  ];
}


  private getNonAdminEditOrgBreadcrumbs(): Breadcrumb[] {
    return [
      {
        label: 'Home',
        url: '/Dashboard',
        disabled: false
      },
      {
        label: 'Edit Organization',
        url: this.router.url,
        disabled: true
      }
    ];
  }

  private addBreadcrumb(route: ActivatedRouteSnapshot, parentUrl: string[], breadcrumbs: Breadcrumb[]) {
    if (!route) return;

    const routeUrl = parentUrl.concat(route.url.map((url) => url.path));

    if (route.data.breadcrumb) {
      if (Array.isArray(route.data.breadcrumb)) {
        route.data.breadcrumb.forEach((data) => {
          const breadcrumb: Breadcrumb = {
            label: get(data, "label", ""),
            url: get(data, "url", "") || "/" + routeUrl.join("/"),
            disabled: get(data, "disabled", false),
          };
          breadcrumbs.push(breadcrumb);
        });
      } else {
        const breadcrumb: Breadcrumb = {
          label: this.getLabel(route.data),
          url: "/" + routeUrl.join("/"),
          disabled: false,
        };
        if (!breadcrumbs.find((item) => item.label === breadcrumb.label)) {
          breadcrumbs.push(breadcrumb);
        }
      }
    }

    if (route.firstChild) {
      this.addBreadcrumb(route.firstChild, routeUrl, breadcrumbs);
    }
  }

  private getLabel(data: Data) {
    return typeof data.breadcrumb === 'function' ? data.breadcrumb(data) : data.breadcrumb;
  }
}